import React, { useState, useEffect } from 'react';
import '../housestyle.css';
import './Apply.css';
import * as Icons from '../Icons/Icons';
import PageHero from '../components/PageHero';
import api from '../API/Api';

const video = 'https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_2.mp4'
const cd_applyUser = 24 * 60 * 60 * 1000;
const cooldown_key = 'smc_apply_cooldowns';

function getCooldowns() {
  try { return JSON.parse(localStorage.getItem(cooldown_key) ?? '{}'); }
  catch { return {}; }
}

function setCooldown(roleId) {
  const cd = getCooldowns();
  cd[roleId] = Date.now();
  localStorage.setItem(cooldown_key, JSON.stringify(cd));
}

function getRemainingTime(roleId) {
  const cd = getCooldowns();
  if (!cd[roleId]) return null;
  const remaining = cd_applyUser - (Date.now() - cd[roleId]);
  if (remaining <= 0) return null;
  const h = Math.floor(remaining / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const ROLES = [
  { id: 'helper', label: 'Helper', icon: Icons.shield, color: 0x369876, desc: 'Support players, moderate chat and handle player reports in-game and in Discord.' },
  { id: 'builder', label: 'Builder', icon: Icons.oakplank, color: 0x52a0e0, desc: 'Design and build POIs, structures, spawn and world features for the server.' },
  { id: 'developer', label: 'Developer', icon: Icons.commandblock, color: 0xaa3b3b, desc: 'Build custom plugins, configure MythicMobs/ExecutableItems and develop server mechanics.' },
  { id: 'media', label: 'Jr. Media', icon: Icons.phantom, color: 0x9b59b6, desc: 'Create YouTube videos, stream on Twitch/TikTok and produce content for SimpleMC. Media members get self-promo commands in Discord and in-game to announce uploads and streams.' },
];

const IDENTITY_FIELDS = [
  { key: 'mcName', label: 'Minecraft Username', placeholder: 'For instance S1mpleDImple', required: true },
  { key: 'discordName', label: 'Discord Username', placeholder: 'For instance simpledimple or #1234', required: true },
];

const PLATFORM_OPTIONS = ['Java', 'Bedrock', 'Both'];
const ACCOUNT_OPTIONS = ['Premium', 'Cracked'];

const helper_questions = {
  general: [
    { key: 'timezone', num: 1, label: "What's your timezone, which continent, and what time is it right now?", type: 'input', placeholder: 'For instance GMT+1, Europe, currently 14:30', required: true },
    { key: 'age', num: 2, label: 'How old are you?', type: 'input', placeholder: 'For instance 17', required: true },
    { key: 'activity', num: 3, label: 'How many hours per week can you be active on the server?', type: 'textarea', placeholder: 'Hours per day / week, which days...', required: true, size: 'short' },
    { key: 'playtime', num: 4, label: 'How long have you been playing on SimpleMC?', type: 'input', placeholder: 'For instance 2 weeks, since launch...', required: true },
    { key: 'knowPlayers', num: 5, label: 'Do you know any current players or staff on SimpleMC?', type: 'input', placeholder: 'Names, or "None"', required: true },
  ],
  experience: [
    { key: 'prevStaff', num: 6, label: 'Have you been staff on any other Minecraft servers?', hint: 'If yes: which server, what role, and roughly how long?', type: 'textarea', required: true, size: 'short' },
    { key: 'whyLeft', num: 7, label: 'Did you leave those servers? If so, why?', hint: 'Be honest: resigned, removed, server closed, etc.', type: 'textarea', required: true, size: 'short' },
    { key: 'currentStaff', num: 8, label: 'Are you currently staff on another server right now?', hint: 'If yes, which server and what role', type: 'input', required: true },
    { key: 'strengths', num: 9, label: 'What do you think makes you a good staff member?', hint: 'Be specific: communication, patience, experience, etc.', type: 'textarea', required: true, size: 'short' },
  ],
  situational: [
    { key: 'spammer', num: 10, label: 'A player is spamming chat and ignoring your warnings. What do you do?', type: 'textarea', required: true },
    { key: 'hacker', num: 11, label: "A player reports a hacker but you didn't witness it yourself. How do you handle this?", type: 'textarea', required: true },
    { key: 'staffAbuse', num: 12, label: 'You notice a fellow staff member abusing their permissions. What steps do you take?', type: 'textarea', required: true },
    { key: 'conflict', num: 13, label: 'Two players are arguing aggressively in chat and it is escalating. How do you de-escalate?', type: 'textarea', required: true },
  ],
  final: [
    { key: 'whyUs', num: 14, label: 'Why do you specifically want to be staff on SimpleMC, and not another server?', type: 'textarea', required: true, size: 'tall' },
    { key: 'extra', num: 15, label: 'Anything else you want us to know about you?', type: 'textarea', required: false, placeholder: 'Optional' },
  ],
};

const builder_questions = {
  general: [
    { key: 'timezone', num: 1, label: "What's your timezone, which continent, and what time is it right now?", type: 'input', placeholder: 'For instance GMT+1, Europe, currently 14:30', required: true },
    { key: 'age', num: 2, label: 'How old are you?', type: 'input', placeholder: 'For instance 17', required: true },
    { key: 'activity', num: 3, label: 'How many hours per week can you dedicate to building for the server?', type: 'textarea', placeholder: 'Hours per day / week, which days...', required: true, size: 'short' },
    { key: 'knowPlayers', num: 4, label: 'Do you know any current players or staff on SimpleMC?', type: 'input', placeholder: 'Names, or "None"', required: true },
  ],
  experience: [
    { key: 'buildExp', num: 5, label: 'How long have you been building in Minecraft, and what is your experience level?', hint: 'Beginner, intermediate or expert. Be honest', type: 'textarea', required: true },
    { key: 'buildTypes', num: 6, label: 'What styles and types of builds are you most comfortable with?', hint: 'Medieval, modern, organic, terraforming, interiors, large-scale, etc.', type: 'textarea', required: true, size: 'short' },
    { key: 'tools', num: 7, label: 'What tools do you use for building?', hint: 'WorldEdit, FAWE, Litematica, hand-built, etc.', type: 'textarea', required: true, size: 'short' },
    { key: 'portfolio', num: 8, label: 'Do you have a portfolio, screenshots or a showreel of your work?', hint: 'Link an Imgur album, Planet Minecraft, YouTube, Discord screenshots, etc.', type: 'input', required: true },
    { key: 'prevServers', num: 9, label: 'Have you built for any other servers before?', hint: 'Server name, what you built, how long you were there', type: 'textarea', required: true, size: 'short' },
    { key: 'otherServer', num: 10, label: 'Are you currently building for another server right now?', hint: 'If yes, which server', type: 'input', required: true },
  ],
  situational: [
    { key: 'poiIdea', num: 11, label: 'Describe a POI or structure you would build for SimpleMC if given your own choice.', hint: 'Theme, size, gameplay purpose, rough description', type: 'textarea', required: true, size: 'tall' },
    { key: 'feedback', num: 12, label: 'A staff member gives you critical feedback on your build. How do you respond?', type: 'textarea', required: true },
    { key: 'deadline', num: 13, label: 'You are given a deadline for a build but realise you cannot finish in time. What do you do?', type: 'textarea', required: true },
  ],
  final: [
    { key: 'whyUs', num: 14, label: 'Why do you want to build for SimpleMC specifically?', type: 'textarea', required: true, size: 'tall' },
    { key: 'extra', num: 15, label: 'Anything else you want us to know?', type: 'textarea', required: false, placeholder: 'Optional' },
  ],
};

const developer_questions = {
  general: [
    { key: 'timezone', num: 1, label: "What's your timezone, which continent, and what time is it right now?", type: 'input', placeholder: 'For instance GMT+1, Europe, currently 14:30', required: true },
    { key: 'age', num: 2, label: 'How old are you?', type: 'input', placeholder: 'For instance 20', required: true },
    { key: 'activity', num: 3, label: 'How many hours per week can you work on development for the server?', type: 'textarea', placeholder: 'Hours per day / week, which days...', required: true, size: 'short' },
    { key: 'knowPlayers', num: 4, label: 'Do you know any current players or staff on SimpleMC?', type: 'input', placeholder: 'Names, or "None"', required: true },
  ],
  experience: [
    { key: 'javaExp', num: 5, label: 'Do you have experience with Java development?', hint: 'How long, what projects, any published plugins?', type: 'textarea', required: true },
    { key: 'devTypes', num: 6, label: 'What have you developed before?', hint: 'Custom plugins, Skript scripts, configs, datapacks, etc.', type: 'textarea', required: true, size: 'short' },
    { key: 'pluginKnow', num: 7, label: 'How familiar are you with these plugins: ExecutableItems, MythicMobs, CMI, Citizens?', hint: 'Rate each 1-5 and describe what you have done with them', type: 'textarea', required: true },
    { key: 'customMechs', num: 8, label: 'Have you created custom mobs, items or mechanics using MythicMobs or ExecutableItems?', hint: 'If yes, describe what you made and how complex it was', type: 'textarea', required: true },
    { key: 'tools', num: 9, label: 'What development tools do you use regularly?', hint: 'IntelliJ, VSCode, Git, GitHub, server panel, etc.', type: 'textarea', required: true, size: 'short' },
    { key: 'portfolio', num: 10, label: 'Do you have a Portfolio or examples of your work?', hint: 'GitHub link, plugin download, or description of a past project', type: 'input', required: true },
    { key: 'otherServer', num: 11, label: 'Are you currently developing for another server right now?', hint: 'If yes, which server. Working for multiple servers is fine if you are transparent', type: 'input', required: true },
  ],
  situational: [
    { key: 'bugFound', num: 12, label: 'You discover a dupe exploit or game-breaking bug on the live server. What do you do? (Tip: We have a development Server)', type: 'textarea', required: true },
    { key: 'codeReview', num: 13, label: 'Another developer pushes code that breaks something in production. How do you handle it?', type: 'textarea', required: true },
    { key: 'playerRequest', num: 14, label: 'A staff member asks you to add a feature. You think it is a bad idea. What do you do?', type: 'textarea', required: true },
  ],
  final: [
    { key: 'whyUs', num: 15, label: 'Why do you want to develop for SimpleMC specifically, and not another server?', type: 'textarea', required: true, size: 'tall' },
    { key: 'extra', num: 16, label: 'Anything else you want us to know?', type: 'textarea', required: false, placeholder: 'Optional' },
  ],
};

const media_questions = {
  general: [
    { key: 'timezone', num: 1, label: "What's your timezone, which continent, and what time is it right now?", type: 'input', placeholder: 'For instance GMT+1, Europe, currently 14:30', required: true },
    { key: 'age', num: 2, label: 'How old are you?', type: 'input', placeholder: 'For instance 17', required: true },
    { key: 'platforms', num: 3, label: 'Which platforms do you create content on? Provide your channel or profile links.', type: 'textarea', placeholder: 'YouTube: youtube.com/... | Twitch: twitch.tv/...', required: true, size: 'short' },
    { key: 'audience', num: 4, label: 'How many subscribers / followers do you have across your platforms?', type: 'input', placeholder: 'For instance 500 YouTube subs, 200 Twitch followers', required: true },
    { key: 'activity', num: 5, label: 'How often do you upload or go live, and how many hours per week can you dedicate to SimpleMC content?', type: 'textarea', placeholder: 'Upload schedule, streaming frequency, hours per week...', required: true, size: 'short' },
  ],
  experience: [
    { key: 'contentType', num: 6, label: "What type of content do you create?", hint: "Let's plays, tutorials, highlights, short-form, IRL, etc.", type: 'textarea', required: true },
    { key: 'equipment', num: 7, label: 'What recording and streaming setup do you use?', hint: 'Mic, camera, capture card, software (OBS, Streamlabs, etc.)', type: 'textarea', required: true, size: 'short' },
    { key: 'editingSoftware', num: 8, label: 'What video editing software do you use?', hint: 'Premiere Pro, DaVinci Resolve, CapCut, Sony Vegas, etc.', type: 'input', required: true },
    { key: 'prevServer', num: 9, label: 'Have you created content for other Minecraft servers before?', hint: 'Server name, what content you made, how long', type: 'textarea', required: true, size: 'short' },
    { key: 'bestContent', num: 10, label: 'Link your best or most recent piece of content.', hint: 'YouTube video, Twitch VOD, TikTok, etc.', type: 'input', required: true },
  ],
  situational: [
    { key: 'contentIdea', num: 11, label: 'Describe a video series or stream idea you have specifically for SimpleMC.', type: 'textarea', required: true, size: 'tall' },
    { key: 'ruleViolation', num: 12, label: 'While streaming you accidentally capture a player breaking a server rule. What do you do?', type: 'textarea', required: true },
    { key: 'negativity', num: 13, label: 'You receive negative or toxic comments on your content. How do you handle it?', type: 'textarea', required: true },
  ],
  final: [
    { key: 'whyUs', num: 14, label: 'Why do you want to create content for SimpleMC specifically?', type: 'textarea', required: true, size: 'tall' },
    { key: 'extra', num: 15, label: 'Anything else you want us to know about you?', type: 'textarea', required: false, placeholder: 'Optional' },
  ],
};

const QUESTIONS = {
  helper: helper_questions,
  builder: builder_questions,
  developer: developer_questions,
  media: media_questions,
};

const SECTION_TITLES = {
  helper: { general: 'General', experience: 'Experience', situational: 'Situational', final: '' },
  builder: { general: 'General', experience: 'Build Experience', situational: 'Situational', final: '' },
  developer: { general: 'General', experience: 'Development Experience', situational: 'Situational', final: '' },
  media: { general: 'General', experience: 'Media Experience', situational: 'Situational', final: '' },
};

const LADDER_TRACKS = [
  {
    id: 'moderation', label: 'Moderation', color: '#d28305',
    roles: [
      {
        name: 'Helper', entry: true, color: '#369876', desc: 'Entry level · apply now',
        summary: 'The first line of support. Helpers keep chat friendly, answer questions from new players and pass bigger problems up to the mods.',
        perms: ['Warn players in chat', 'Short mutes (up to 30m)', 'Answer questions & guide new players', 'Handle tickets and player reports'],
        mcCommands: [
          { cmd: '/warn <player> <reason>', desc: 'Give a player an official warning' },
          { cmd: '/mute <player> 30m', desc: 'Mute a player for up to 30 minutes' },
          { cmd: '/helpop', desc: 'Read & answer player help requests' },
        ],
        discordCommands: [
          { cmd: 'Ticket access', desc: 'View and respond to support tickets' },
        ],
      },
      {
        name: 'Jr. Mod', color: '#f2a11f', desc: 'Mute & warn players',
        summary: 'A Helper who has proven themselves. Jr. Mods handle chat moderation on their own and help keep Discord in check.',
        perms: ['All Helper permissions', 'Full mutes & kicks', 'Moderate Discord channels', 'Escalate serious cases to Mods'],
        mcCommands: [
          { cmd: '/mute <player> <time>', desc: 'Mute for any duration' },
          { cmd: '/kick <player> <reason>', desc: 'Kick a player from the server' },
          { cmd: '/clearchat', desc: 'Clear the chat after spam' },
        ],
        discordCommands: [
          { cmd: 'Timeout members', desc: 'Time out rule-breakers in Discord' },
          { cmd: 'Manage messages', desc: 'Delete rule-breaking messages' },
        ],
      },
      {
        name: 'Mod', color: '#d28305', desc: 'Full mod permissions',
        summary: 'Full moderators. Mods handle hackers, temp bans and appeals, and are trusted with the tools to investigate players.',
        perms: ['All Jr. Mod permissions', 'Temporary bans', 'Handle hacker reports & screenshares', 'Review mute/ban appeals'],
        mcCommands: [
          { cmd: '/tempban <player> <time>', desc: 'Temporarily ban a player' },
          { cmd: '/vanish', desc: 'Go invisible to observe suspects' },
          { cmd: '/invsee <player>', desc: 'Inspect a player\'s inventory' },
        ],
        discordCommands: [
          { cmd: 'Kick members', desc: 'Remove rule-breakers from Discord' },
          { cmd: 'Appeal channel access', desc: 'Review mute & ban appeals' },
        ],
      },
      {
        name: 'Sr. Mod', color: '#a070e0', desc: 'Bans + mentoring',
        summary: 'Senior moderators mentor the junior team and are trusted with permanent punishments and staff application reviews.',
        perms: ['All Mod permissions', 'Permanent bans', 'Mentor Helpers & Jr. Mods', 'Help review staff applications'],
        mcCommands: [
          { cmd: '/ban <player> <reason>', desc: 'Permanently ban a player' },
          { cmd: '/history <player>', desc: 'View a player\'s full punishment history' },
        ],
        discordCommands: [
          { cmd: 'Ban members', desc: 'Permanently ban from Discord' },
          { cmd: 'Staff-apps access', desc: 'Read and vote on staff applications' },
        ],
      },
      {
        name: 'H-Mod', color: '#8e44ad', desc: 'Leads the mod team',
        summary: 'Head of moderation. The H-Mod runs the whole mod team, decides promotions and has the final say on punishments.',
        perms: ['All Sr. Mod permissions', 'Leads the moderation team', 'Handles promotions & demotions', 'Final say on punishments'],
        mcCommands: [
          { cmd: '/unban <player>', desc: 'Overturn any punishment' },
          { cmd: '/staff', desc: 'Manage staff ranks in-game' },
        ],
        discordCommands: [
          { cmd: 'Manage roles', desc: 'Promote and demote mod team members' },
          { cmd: 'Staff-leadership access', desc: 'Private leadership channels' },
        ],
      },
    ],
  },
  {
    id: 'building', label: 'Building', color: '#52a0e0',
    roles: [
      {
        name: 'Builder', entry: true, color: '#52a0e0', desc: 'Entry level · apply now',
        summary: 'Builders create the POIs, structures and world features that make SimpleMC unique. Work happens on a dedicated build server.',
        perms: ['WorldEdit / FAWE on the build server', 'Build POIs, structures & spawn features', 'Access to the build team Discord', 'Credited in-game for your builds'],
        mcCommands: [
          { cmd: '//wand', desc: 'WorldEdit selection wand (build server)' },
          { cmd: '//copy, //paste', desc: 'Full WorldEdit / FAWE toolkit' },
          { cmd: '/gamemode creative', desc: 'Creative mode on the build server' },
        ],
        discordCommands: [
          { cmd: 'Build-team access', desc: 'Private build team channels & projects' },
        ],
      },
    ],
  },
  {
    id: 'development', label: 'Development', color: '#aa3b3b',
    roles: [
      {
        name: 'Developer', entry: true, color: '#aa3b3b', desc: 'Entry level · apply now',
        summary: 'Developers build the custom mechanics behind SimpleMC: plugins, MythicMobs, ExecutableItems and configs. All work starts on the dev server.',
        perms: ['Development server access', 'Create MythicMobs & ExecutableItems content', 'Write plugins, Skript & configs', 'Access to the dev team Discord'],
        mcCommands: [
          { cmd: '/mm', desc: 'MythicMobs — custom mobs & bosses (dev server)' },
          { cmd: '/ei', desc: 'ExecutableItems — custom items (dev server)' },
          { cmd: '/sk reload', desc: 'Reload Skript scripts & configs' },
        ],
        discordCommands: [
          { cmd: 'Dev-team access', desc: 'Private dev channels, GitHub & task board' },
        ],
      },
      {
        name: 'H-Dev', color: '#2ecc71', desc: 'Leads the dev team',
        summary: 'Head of development. The H-Dev reviews all dev work, decides what goes live and owns the development roadmap.',
        perms: ['All Developer permissions', 'Push changes to the live server', 'Reviews all dev work before release', 'Leads the development roadmap'],
        mcCommands: [
          { cmd: '/plugman reload', desc: 'Manage plugins on the live server' },
          { cmd: 'Console access', desc: 'Full access to the production console' },
        ],
        discordCommands: [
          { cmd: 'Manage dev roles', desc: 'Runs the dev team & assigns tasks' },
          { cmd: 'Staff-leadership access', desc: 'Private leadership channels' },
        ],
      },
    ],
  },
  {
    id: 'media', label: 'Media', color: '#9b59b6',
    roles: [
      {
        name: 'Jr. Media', entry: true, color: '#9b59b6', desc: 'Apply now · self-promo commands',
        summary: 'Starting content creators. Jr. Media members can promote their SimpleMC videos and streams both in-game and in Discord.',
        perms: ['Self-promo commands in Discord & in-game', 'Media role and badge in Discord', 'Announce uploads & streams'],
        mcCommands: [
          { cmd: '/promo <link>', desc: 'Announce your video or stream in chat' },
          { cmd: '/media', desc: 'Show your media badge & channel links' },
        ],
        discordCommands: [
          { cmd: '#self-promo posting', desc: 'Share your uploads in the promo channel' },
          { cmd: 'Media role', desc: 'Media badge & colour in Discord' },
        ],
      },
      {
        name: 'Media', color: '#8e44ad', desc: 'Established creator',
        summary: 'Established creators who consistently make SimpleMC content. Media members get bigger reach and early access to new content.',
        perms: ['All Jr. Media permissions', 'Expanded promo access & pings', 'Early access to new content for videos'],
        mcCommands: [
          { cmd: '/promo <link>', desc: 'Promo with higher visibility & shorter cooldown' },
          { cmd: '/preview', desc: 'Early access to unreleased content for videos' },
        ],
        discordCommands: [
          { cmd: 'Announcement pings', desc: 'Ping the media-updates role on uploads' },
        ],
      },
      {
        name: 'Streamer', color: '#6c3483', desc: 'Live streaming partner',
        summary: 'Live streaming partners. Streams are announced automatically and Streamers get giveaway keys to reward their viewers.',
        perms: ['All Media permissions', 'Automatic live announcements', 'Full promo commands', 'Giveaway keys for your streams'],
        mcCommands: [
          { cmd: '/live', desc: 'Announce your stream server-wide' },
          { cmd: '/streamkeys', desc: 'Claim giveaway keys for your viewers' },
        ],
        discordCommands: [
          { cmd: 'Auto live announcements', desc: 'Going live is announced automatically' },
          { cmd: 'Streamer role', desc: 'Featured in the live-now channel' },
        ],
      },
    ],
  },
  {
    id: 'leadership', label: 'Leadership', color: '#e84c3d',
    roles: [
      {
        name: 'Admin', color: '#e84c3d', desc: 'Oversees the team',
        summary: 'Admins oversee every staff team, resolve staff conflicts and keep the whole server running smoothly.',
        perms: ['Full server permissions', 'Oversees all staff teams', 'Handles staff conflicts & reports'],
        mcCommands: [
          { cmd: 'All commands', desc: 'Full permissions on every server' },
        ],
        discordCommands: [
          { cmd: 'Administrator', desc: 'Full Discord administrator permissions' },
        ],
      },
      {
        name: 'Manager', color: '#f0ab20', desc: 'Day-to-day operations',
        summary: 'The Manager runs SimpleMC: day-to-day operations, partnerships and the final call on every big decision.',
        perms: ['Runs day-to-day operations', 'Partnerships & server direction', 'Final decisions on everything'],
        mcCommands: [
          { cmd: 'All commands', desc: 'Owner-level access everywhere' },
        ],
        discordCommands: [
          { cmd: 'Server owner', desc: 'Full control of the Discord server' },
        ],
      },
    ],
  },
];

function buildPayload(role, identity, answers) {
  const qs = Object.values(QUESTIONS[role]).flat();
  return {
    role,
    mcName:      identity.mcName,
    discord:     identity.discordName,
    platform:    identity.platform,
    accountType: identity.accountType,
    answers: qs
      .filter(q => answers[q.key]?.trim())
      .map(q => ({ question: `${q.num}. ${q.label}`, answer: answers[q.key].trim() })),
  };
}

function validate(role, identity, answers) {
  if (!identity.mcName?.trim()) return 'Please enter your Minecraft username.';
  if (!identity.discordName?.trim()) return 'Please enter your Discord username.';
  if (!identity.platform) return 'Please select your platform (Java, Bedrock, or Both).';
  if (!identity.accountType) return 'Please select your account type (Premium or Cracked).';
  const ageVal = parseInt(answers.age, 10);
  if (!isNaN(ageVal) && ageVal < 15) return 'You must be at least 15 years old to apply.';
  const qs = Object.values(QUESTIONS[role]).flat();
  for (const q of qs) {
    if (q.required && !answers[q.key]?.trim()) return `Please answer question ${q.num}.`;
  }
  return null;
}

function StepBar({ step }) {
  return (
    <div className="apply-steps">
      {['Choose Role', 'Application', 'Done'].map((label, i) => {
        const n = i + 1;
        const cls = n < step ? 'done' : n === step ? 'active' : '';
        return (
          <React.Fragment key={label}>
            <div className={`apply-step ${cls}`}>
              <div className="apply-step-num">{n < step ? '✓' : n}</div>
              {label}
            </div>
            {i < 2 && <div className="apply-step-line" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FormField({ q, value, onChange }) {
  return (
    <div className="apply-field">
      <div className="apply-field-label">
        <span className="apply-field-num">{q.num}.</span>
        {q.label}
        {q.required && <span className="apply-req"> </span>}
      </div>
      {q.hint && <div className="apply-hint">{q.hint}</div>}
      {q.type === 'input' ? (
        <input
          className="apply-input"
          placeholder={q.placeholder ?? ''}
          value={value}
          onChange={e => onChange(q.key, e.target.value)}
        />
      ) : (
        <>
          <textarea
            className={`apply-textarea ${q.size ?? ''}`}
            placeholder={q.placeholder ?? ''}
            value={value}
            onChange={e => onChange(q.key, e.target.value)}
          />
          {q.required && (
            <div className={`apply-char ${value.length >= 40 ? 'ok' : ''}`}>
              {value.length} chars {value.length >= 40 ? '✓' : '(min 40)'}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RoleIcon({ src, alt }) {
  return <img src={src} alt={alt} className="apply-role-icon-img" />;
}

function RoleModal({ role, track, onClose }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="apply-role-modal-overlay" onClick={onClose}>
      <div className="apply-role-modal" style={{ '--role-c': role.color }} onClick={e => e.stopPropagation()}>
        <button className="apply-role-modal-close" onClick={onClose}>✕</button>
        <div className="apply-role-modal-head">
          <span className="apply-role-modal-track" style={{ color: track.color }}>{track.label}</span>
          <h3 className="apply-role-modal-name">
            {role.name}
            {role.entry && <span className="apply-role-modal-entry">Entry level</span>}
          </h3>
          <p className="apply-role-modal-summary">{role.summary}</p>
        </div>

        <div className="apply-role-modal-section">
          <div className="apply-role-modal-section-title">What they can do</div>
          <ul className="apply-role-modal-perms">
            {role.perms.map(p => <li key={p}>{p}</li>)}
          </ul>
        </div>

        <div className="apply-role-modal-section">
          <div className="apply-role-modal-section-title">In-game commands</div>
          <div className="apply-role-modal-cmds">
            {role.mcCommands.map(c => (
              <div key={c.cmd} className="apply-role-modal-cmd">
                <code>{c.cmd}</code>
                <span>{c.desc}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="apply-role-modal-section">
          <div className="apply-role-modal-section-title">Discord</div>
          <div className="apply-role-modal-cmds">
            {role.discordCommands.map(c => (
              <div key={c.cmd} className="apply-role-modal-cmd">
                <code>{c.cmd}</code>
                <span>{c.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StaffLadder() {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null);
  return (
    <div className="apply-ladder">
      <button className="apply-ladder-toggle" onClick={() => setOpen(o => !o)}>
        <span className="apply-ladder-toggle-label">
          <span className="apply-ladder-dot" />
          Staff Progression Ladder
        </span>
        <span className="apply-ladder-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="apply-ladder-body">
          <p className="apply-ladder-intro">
            All staff start at an entry-level position. You can only apply for the roles below — higher ranks are earned through promotion.
            Click a role for its full info, commands and perks.
          </p>
          <div className="apply-ladder-tracks">
            {LADDER_TRACKS.map(track => (
              <div key={track.id} className="apply-ladder-track">
                <div className="apply-ladder-track-label" style={{ color: track.color }}>{track.label}</div>
                <div className="apply-ladder-roles">
                  {track.roles.map(role => (
                    <button
                      key={role.name}
                      type="button"
                      className={`apply-ladder-role ${role.entry ? 'entry' : ''}`}
                      style={{ '--role-c': role.color }}
                      onClick={() => setModal({ role, track })}
                    >
                      <div className="apply-ladder-role-head">
                        <div>
                          <div className="apply-ladder-role-name">{role.name}</div>
                          <div className="apply-ladder-role-desc">{role.desc}</div>
                        </div>
                        <span className="apply-ladder-role-chevron">ⓘ</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="apply-ladder-note">
            <span className="apply-ladder-note-dot entry" /> Entry level &nbsp;·&nbsp;
            <span className="apply-ladder-note-dot" /> Promoted internally
          </div>
        </div>
      )}
      {modal && <RoleModal role={modal.role} track={modal.track} onClose={() => setModal(null)} />}
    </div>
  );
}

function PageHeader() {
  return (
    <PageHero
      video={video}
      eyebrow="Staff Team"
      title="Apply for "
      em="Staff"
      subtitle="Fill out the form! Your application will get sent to our Discord staff channel. Low-effort answers will be declined."
    />
  );
}

export default function Apply() {
  const [step, setStep] = useState(1);
  const [role, setRole] = useState('');
  const [identity, setIdentity] = useState({ mcName: '', discordName: '', platform: '', accountType: '' });
  const [answers, setAnswers] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [cooldowns, setCooldowns] = useState({});

  useEffect(() => { setCooldowns(getCooldowns()); }, []);

  const setAnswer = (key, val) => { setAnswers(a => ({ ...a, [key]: val })); setError(''); };
  const setIdField = (key, val) => { setIdentity(i => ({ ...i, [key]: val })); setError(''); };

  async function submit() {
    const err = validate(role, identity, answers);
    if (err) { setError(err); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setSending(true); setError('');
    try {
      const res = await api('submitapplication', buildPayload(role, identity, answers));
      if (res.isSuccess) {
        setCooldown(role);
        setCooldowns(getCooldowns());
        setStep(3);
        window.scrollTo(0, 0);
      } else {
        setError(res.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  }

  function reset() {
    setStep(1); setRole('');
    setIdentity({ mcName: '', discordName: '', platform: '', accountType: '' });
    setAnswers({}); setError('');
  }

  const roleObj = ROLES.find(r => r.id === role);
  const questions = role ? QUESTIONS[role] : {};

  const requiredQs = Object.values(questions).flat().filter(q => q.required);
  const answeredCount = requiredQs.filter(q => answers[q.key]?.trim()).length;
  const sectionProgress = key => {
    const req = (questions[key] ?? []).filter(q => q.required);
    return { done: req.filter(q => answers[q.key]?.trim()).length, total: req.length };
  };

  if (step === 1) return (
    <>
      <PageHeader />
      <div className="container apply-form-wrap">
        <StepBar step={1} />
        <StaffLadder />
        <div className="sec-row"><span className="sec-eyebrow sec-eyebrow--left">Choose your role</span></div>
        <div className="apply-role-grid">
          {ROLES.map(r => {
            const remaining = getRemainingTime(r.id);
            return (
              <div
                key={r.id}
                className={`apply-role-card ${role === r.id ? 'selected' : ''} ${remaining ? 'cooldown' : ''}`}
                style={{ '--rc': `#${r.color.toString(16).padStart(6, '0')}` }}
                onClick={() => !remaining && setRole(r.id)}
              >
                <div className="apply-role-card-bar" />
                <RoleIcon src={r.icon} alt={r.label} />
                <div className="apply-role-name">{r.label}</div>
                <div className="apply-role-desc">{r.desc}</div>
                {remaining && (
                  <span className="apply-cooldown-badge">Try again in {remaining}</span>
                )}
              </div>
            );
          })}
        </div>
        <button
          className={`btn btn-primary ${!role ? 'apply-btn-disabled' : ''}`}
          onClick={() => role && setStep(2)}
        >
          Next Step
        </button>
      </div>
    </>
  );

  if (step === 3) return (
    <>
      <PageHeader />
      <div className="container">
        <div className="apply-success">
          <div className="apply-success-icon">
            <RoleIcon src={roleObj?.icon} alt={roleObj?.label} />
          </div>
          <h2>Application Submitted</h2>
          <p>
            Your <strong className="apply-success-highlight">{roleObj?.label}</strong> application
            has been posted to our staff Discord channel. We will review it and reach out via Discord, so keep your DMs open.
          </p>
          <div className="apply-success-summary">
            {[
              ['Role', roleObj?.label],
              ['Minecraft Username', identity.mcName],
              ['Discord Username', identity.discordName],
              ['Timezone', answers.timezone],
            ].map(([l, v], i) => (
              <div key={i} className="apply-success-row">
                <span className="apply-sr-label">{l}</span>
                <span className="apply-sr-val">{v ?? '-'}</span>
              </div>
            ))}
          </div>
          <button className="btn btn-secondary" onClick={reset}>
            Submit Another Application
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <PageHeader />
      <div className="container apply-form-wrap">
        <StepBar step={2} />

        <div className="apply-role-bar">
          <div className="apply-role-bar-left">
            <span className="sec-eyebrow sec-eyebrow--left">Your Application</span>
            <span className="apply-role-pill">
              <RoleIcon src={roleObj?.icon} alt={roleObj?.label} />
              {roleObj?.label}
            </span>
          </div>
          <button className="btn btn-secondary apply-change-btn" onClick={() => setStep(1)}>
            Go back
          </button>
        </div>

        <div className="apply-progress">
          <div className="apply-progress-top">
            <span>Progress</span>
            <span className={answeredCount === requiredQs.length ? 'apply-progress-done' : ''}>
              {answeredCount} / {requiredQs.length} required questions
            </span>
          </div>
          <div className="apply-progress-bar">
            <div
              className="apply-progress-fill"
              style={{ width: `${requiredQs.length ? (answeredCount / requiredQs.length) * 100 : 0}%` }}
            />
          </div>
        </div>

        <div className="apply-warning">
          Application with low-effort answers will not be taken seriously, so please put thought into your responses. We will reach out via Discord, so keep your DMs open!
        </div>

        {error && <div className="apply-error">{error}</div>}

        <div className="apply-identity">
          <div className="apply-identity-title">
            Your Details
            <span className="apply-min-age-badge">Min Age: 15+</span>
          </div>
          {IDENTITY_FIELDS.map(f => (
            <div key={f.key} className="apply-field">
              <div className="apply-field-label">
                {f.label}
                <span className="apply-req"></span>
              </div>
              <input
                className="apply-input"
                placeholder={f.placeholder}
                value={identity[f.key]}
                onChange={e => setIdField(f.key, e.target.value)}
              />
            </div>
          ))}
          <div className="apply-field apply-toggle-field">
            <div className="apply-field-label">Platform <span className="apply-req"></span></div>
            <div className="apply-toggle-group">
              {PLATFORM_OPTIONS.map(opt => (
                <button
                  key={opt}
                  type="button"
                  className={`apply-toggle-btn ${identity.platform === opt ? 'active' : ''}`}
                  onClick={() => { setIdField('platform', opt); setError(''); }}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div className="apply-field apply-toggle-field">
            <div className="apply-field-label">Account Type <span className="apply-req"></span></div>
            <div className="apply-toggle-group">
              {ACCOUNT_OPTIONS.map(opt => (
                <button
                  key={opt}
                  type="button"
                  className={`apply-toggle-btn ${identity.accountType === opt ? 'active' : ''}`}
                  onClick={() => { setIdField('accountType', opt); setError(''); }}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>

        {Object.entries(questions).map(([sectionKey, qs]) => {
          const prog = sectionProgress(sectionKey);
          return (
          <div key={sectionKey} className="apply-section">
            {SECTION_TITLES[role][sectionKey] && (
              <div className="sec-row apply-section-head">
                <span className="sec-eyebrow sec-eyebrow--left">{SECTION_TITLES[role][sectionKey]}</span>
                {prog.total > 0 && (
                  <span className={`apply-section-count ${prog.done === prog.total ? 'done' : ''}`}>
                    {prog.done}/{prog.total}
                  </span>
                )}
              </div>
            )}
            {qs.map(q => (
              <FormField
                key={q.key}
                q={q}
                value={answers[q.key] ?? ''}
                onChange={setAnswer}
              />
            ))}
          </div>
          );
        })}

        {error && <div className="apply-error">{error}</div>}

        <div className="apply-submit-row">
          <button
            className={`btn btn-primary apply-submit-btn ${sending ? 'apply-btn-disabled' : ''}`}
            onClick={submit}
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Submit Application'}
          </button>
          <span className="apply-submit-note">After sending open a ticket if you haven't already</span>
        </div>

        <div className="apply-disclaimer">
          By submitting you confirm all information is accurate. Lying about past bans or experience is an automatic disqualification. We will reach out via Discord, so make sure your DMs are open or that you have a ticket open.
        </div>
      </div>
    </>
  );
}
